/**
 * BuchUbungenPanel — replaces MaterialComments for Buch-Übungen materials.
 *
 * Privacy model:
 *   - Each student sees only their OWN submission and the teacher's
 *     correction on it. They never see other students' work.
 *   - The teacher sees a list of all students who submitted, with their
 *     work and a "Korrektur schreiben" panel per submission.
 *
 * The student composer mirrors MaterialComments (text + audio + file picker)
 * but writes to `/api/materials/{id}/submissions` and supports re-editing
 * while status === 'draft'. Once corrected, the entry becomes read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, FloppyDisk, CheckCircle, Lock, Trash, FileArrowUp, FileText,
  Spinner, MicrophoneStage, Pencil, DownloadSimple,
} from '@phosphor-icons/react';
import AudioRecorder from './AudioRecorder';
import FilePreview from './FilePreview';
import { useAuth } from '../contexts/AuthContext';
import { chunkedUpload } from '../utils/chunkedUpload';
import { fileUrl as buildFileUrl } from '../utils/fileUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (u) => buildFileUrl(u);

function statusBadge(status, isDe) {
  if (status === 'corrected') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
        <CheckCircle size={11} weight="fill" />
        {isDe ? 'Korrigiert' : 'Corregido'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
      <Pencil size={11} weight="fill" />
      {isDe ? 'Entwurf' : 'Borrador'}
    </span>
  );
}

// ── Student composer ─────────────────────────────────────────────────────────
function StudentComposer({ materialId, existing, onSaved, isDe, previewOnly = false }) {
  const [text, setText] = useState(existing?.body || '');
  const [audioUrl, setAudioUrl] = useState(existing?.audio_url || null);
  const [files, setFiles] = useState(existing?.files || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const locked = existing?.status === 'corrected';

  const uploadFile = async (f) => {
    setUploading(true);
    try {
      const stored = await chunkedUpload(f, 'content');
      setFiles(prev => [...prev, {
        url: stored.storage_path,
        name: stored.filename || f.name,
        type: stored.content_type || f.type,
        size: stored.size || f.size,
      }]);
    } catch (e) {
      toast.error(isDe ? 'Upload fehlgeschlagen' : 'Error subiendo archivo');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!text.trim() && !audioUrl && files.length === 0) {
      return toast.error(isDe ? 'Schreib etwas, lade eine Datei oder nimm ein Audio auf' : 'Escribe algo, sube un archivo o graba un audio');
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/materials/${materialId}/submissions`,
        { body: text, audio_url: audioUrl, files },
        { withCredentials: true },
      );
      toast.success(existing ? (isDe ? 'Gespeichert' : 'Guardado') : (isDe ? 'Abgegeben' : 'Entregado'));
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (locked) {
    return (
      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
        <Lock size={12} weight="fill" />
        {isDe ? 'Diese Aufgabe wurde bereits korrigiert und ist gesperrt.' : 'Esta tarea ya fue corregida y está bloqueada.'}
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2 bg-[#FAF9F6] rounded-lg p-3 border border-stone-200" data-testid={`submission-composer-${materialId}`}>
      <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">
        {existing ? (isDe ? 'Deine Antwort (bearbeiten)' : 'Tu respuesta (editar)') : (isDe ? 'Deine Antwort' : 'Tu respuesta')}
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={4}
        placeholder={isDe ? 'Schreib deine Antwort hier…' : 'Escribe tu respuesta aquí…'}
        className="w-full border border-stone-300 rounded px-2 py-1.5 text-sm bg-white"
        data-testid={`submission-text-${materialId}`}
      />
      <AudioRecorder
        compact
        testId={`submission-audio-${materialId}`}
        maxSeconds={300}
        onUploaded={(f) => { setAudioUrl(f.url); toast.success(isDe ? 'Audio angehängt' : 'Audio adjuntado'); }}
      />
      {audioUrl && (
        <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg p-2">
          <audio controls preload="metadata" src={fileUrl(audioUrl)} className="h-9 max-w-md w-full rounded-full" style={{ maxHeight: '36px' }} />
          <button onClick={() => setAudioUrl(null)} className="text-stone-400 hover:text-red-500 p-1">
            <Trash size={13} />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-stone-300 rounded cursor-pointer hover:bg-stone-50 text-xs">
          <FileArrowUp size={13} weight="bold" />
          {isDe ? 'Datei anhängen' : 'Adjuntar archivo'}
          <input
            type="file"
            accept="*/*"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
            disabled={uploading || busy}
            data-testid={`submission-file-input-${materialId}`}
          />
        </label>
        {uploading && <Spinner size={13} className="animate-spin text-stone-500" />}
        {files.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-2 py-1 text-xs">
            <FileText size={12} />
            <span className="truncate max-w-[140px]" title={f.name}>{f.name}</span>
            <button onClick={() => removeFile(i)} className="text-stone-400 hover:text-red-500"><Trash size={11} /></button>
          </span>
        ))}
      </div>
      {previewOnly ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            disabled
            className="inline-flex items-center gap-1 bg-stone-300 text-white px-3 py-1.5 rounded text-sm font-semibold cursor-not-allowed"
            data-testid={`submission-submit-${materialId}`}
          >
            <PaperPlaneTilt size={13} weight="fill" />
            {isDe ? 'Abgeben' : 'Entregar'}
          </button>
          <span className="text-[11px] text-emerald-700 font-semibold italic">
            {isDe ? 'Vorschau-Modus — Lehrer können nicht abgeben' : 'Modo previsualización — los profes no entregan'}
          </span>
        </div>
      ) : (
        <button
          onClick={submit}
          disabled={busy || uploading}
          className="inline-flex items-center gap-1 bg-[#AB1C24] hover:bg-[#8A151C] disabled:bg-stone-300 text-white px-3 py-1.5 rounded text-sm font-semibold"
          data-testid={`submission-submit-${materialId}`}
        >
          {busy ? <Spinner size={13} className="animate-spin" /> : (existing ? <FloppyDisk size={13} weight="fill" /> : <PaperPlaneTilt size={13} weight="fill" />)}
          {existing ? (isDe ? 'Speichern' : 'Guardar') : (isDe ? 'Abgeben' : 'Entregar')}
        </button>
      )}
    </div>
  );
}

// ── Teacher correction panel ─────────────────────────────────────────────────
function TeacherCorrection({ materialId, submission, onSaved, isDe, materialCategory }) {
  const [text, setText] = useState(submission.correction_body || '');
  const [audioUrl, setAudioUrl] = useState(submission.correction_audio_url || null);
  const [busy, setBusy] = useState(false);
  // Numeric 1–10 score is only meaningful for the human-judged categories
  // (Sprechen / Schreiben). For the auto-graded ones the per-class stats panel
  // already counts correct/incorrect from exercise_progress so showing a
  // teacher score box would be redundant and confusing.
  const isManualCategory = materialCategory === 'speaking' || materialCategory === 'writing';
  const [score, setScore] = useState(submission.teacher_score || '');
  const [savingScore, setSavingScore] = useState(false);
  const saveScore = async (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10) {
      return toast.error(isDe ? 'Bewertung 1–10' : 'Puntuación 1–10');
    }
    setSavingScore(true);
    try {
      await axios.patch(
        `${API}/teacher/submissions/${submission.submission_id}/score`,
        { score: n },
        { withCredentials: true },
      );
      toast.success(isDe ? `Bewertung gespeichert (${n}/10)` : `Puntuación guardada (${n}/10)`);
      onSaved?.({ ...submission, teacher_score: n });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSavingScore(false);
    }
  };
  const submit = async () => {
    if (!text.trim() && !audioUrl) return toast.error(isDe ? 'Schreib eine Korrektur oder hänge ein Audio an' : 'Escribe la corrección o adjunta un audio');
    setBusy(true);
    try {
      const res = await axios.patch(
        `${API}/materials/${materialId}/submissions/${submission.submission_id}/correction`,
        { correction_body: text, correction_audio_url: audioUrl },
        { withCredentials: true },
      );
      toast.success(isDe ? 'Korrektur gesendet' : 'Corrección enviada');
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 bg-stone-50 border border-stone-200 rounded-lg p-2 space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#57534E]">
        {isDe ? 'Korrektur an den Schüler' : 'Corrección al alumno'}
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        placeholder={isDe ? 'Anmerkungen…' : 'Comentarios…'}
        className="w-full border border-stone-300 rounded px-2 py-1.5 text-xs bg-white"
        data-testid={`correction-text-${submission.submission_id}`}
      />
      <AudioRecorder
        compact
        testId={`correction-audio-${submission.submission_id}`}
        maxSeconds={300}
        onUploaded={(f) => setAudioUrl(f.url)}
      />
      {audioUrl && (
        <audio controls preload="metadata" src={fileUrl(audioUrl)} className="h-9 max-w-md w-full rounded-full" style={{ maxHeight: '36px' }} />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white px-3 py-1.5 rounded text-xs font-semibold"
          data-testid={`correction-save-${submission.submission_id}`}
        >
          {busy ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
          {submission.status === 'corrected' ? (isDe ? 'Korrektur aktualisieren' : 'Actualizar corrección') : (isDe ? 'Korrektur senden' : 'Enviar corrección')}
        </button>
        {isManualCategory && (
          <div
            className="inline-flex items-center gap-1.5 ml-auto bg-white border border-amber-200 rounded-full px-2 py-1"
            data-testid={`score-widget-${submission.submission_id}`}
            title={isDe ? 'Schnellbewertung 1–10' : 'Valoración rápida 1–10'}
          >
            <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
              {isDe ? 'Note' : 'Nota'}
            </span>
            <input
              type="number"
              min={1}
              max={10}
              value={score}
              onChange={e => setScore(e.target.value)}
              onBlur={e => { const v = e.target.value; if (v && Number(v) !== submission.teacher_score) saveScore(v); }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              className="w-10 text-center text-xs border-0 focus:outline-none bg-transparent font-bold text-amber-700"
              data-testid={`score-input-${submission.submission_id}`}
              disabled={savingScore}
              placeholder="–"
            />
            <span className="text-[10px] text-stone-400">/10</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Submission card (shared, used by both student & teacher views) ───────────
function SubmissionCard({ submission, isTeacher, materialId, onChanged, isDe }) {
  // Teachers must be able to wipe a broken submission (e.g. a student
  // submitted a 0-second audio by mistake). Owner can also delete their
  // own draft as long as it hasn't been corrected yet — the backend
  // enforces both rules.
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    const confirmMsg = isDe
      ? 'Diese Abgabe (inkl. Audio/Datei) endgültig löschen?'
      : '¿Borrar definitivamente esta entrega (con audio/archivos)?';
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/materials/${materialId}/submissions/${submission.submission_id}`,
        { withCredentials: true },
      );
      toast.success(isDe ? 'Abgabe gelöscht' : 'Entrega borrada');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`submission-${submission.submission_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {isTeacher && (
            <span className="font-semibold text-sm text-[#292524]">{submission.user_name}</span>
          )}
          {statusBadge(submission.status, isDe)}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#A8A29E]">
            {new Date(submission.updated_at).toLocaleString(isDe ? 'de-DE' : 'es-ES', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
          {isTeacher && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 disabled:opacity-50"
              title={isDe ? 'Abgabe löschen' : 'Borrar entrega'}
              data-testid={`del-submission-${submission.submission_id}`}
            >
              {deleting ? <Spinner size={14} className="animate-spin" /> : <Trash size={14} />}
            </button>
          )}
        </div>
      </div>
      {submission.body && (
        <p className="text-sm text-[#292524] whitespace-pre-wrap break-words bg-stone-50 rounded p-2">{submission.body}</p>
      )}
      {submission.audio_url && (
        <audio controls preload="metadata" src={fileUrl(submission.audio_url)} className="h-9 max-w-md w-full rounded-full" style={{ maxHeight: '36px' }} />
      )}
      {submission.files?.length > 0 && (
        <div className="space-y-2">
          {submission.files.map((f, i) => (
            <FilePreview key={i} file={f} lang={isDe ? 'de' : 'es'} compact />
          ))}
        </div>
      )}
      {submission.status === 'corrected' && (submission.correction_body || submission.correction_audio_url) && (
        <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded p-2 space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1">
            <CheckCircle size={11} weight="fill" />
            {isDe ? 'Korrektur' : 'Corrección'}
            {submission.corrected_by_name && <span className="font-normal text-emerald-700">· {submission.corrected_by_name}</span>}
          </p>
          {submission.correction_body && (
            <p className="text-sm text-emerald-900 whitespace-pre-wrap break-words">{submission.correction_body}</p>
          )}
          {submission.correction_audio_url && (
            <audio controls preload="metadata" src={fileUrl(submission.correction_audio_url)} className="h-9 max-w-md w-full rounded-full" style={{ maxHeight: '36px' }} />
          )}
        </div>
      )}
      {isTeacher && (
        <TeacherCorrection materialId={materialId} submission={submission} onSaved={onChanged} isDe={isDe} />
      )}
    </div>
  );
}

// ── Public component ─────────────────────────────────────────────────────────
export default function BuchUbungenPanel({ materialId, isDe }) {
  const { user, demoMode } = useAuth();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Teachers can flip this to "Als Schüler ansehen" to see the exact
  // composer (textarea + AudioRecorder + attach-file) that students get
  // when delivering this exercise. Defaults to the global demoMode so the
  // platform-wide Lehrer-Demo applies here too.
  const [studentMode, setStudentMode] = useState(demoMode);
  useEffect(() => { setStudentMode(demoMode); }, [demoMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/materials/${materialId}/submissions`, { withCredentials: true });
      setSubmissions(res.data?.submissions || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [materialId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="mt-2 text-xs text-[#A8A29E]"><Spinner size={12} className="animate-spin inline mr-1" /> {isDe ? 'Lade…' : 'Cargando…'}</div>;
  }

  if (isTeacher) {
    return (
      <div className="mt-3 space-y-2" data-testid={`buch-uebungen-panel-${materialId}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">
            {studentMode
              ? (isDe ? 'Vorschau (so liefern die Schüler ab)' : 'Vista previa (así entregarán los alumnos)')
              : (isDe ? `Abgaben (${submissions.length})` : `Entregas (${submissions.length})`)}
          </p>
          <button
            onClick={() => setStudentMode(s => !s)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              studentMode
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'bg-white text-[#292524] border-stone-300 hover:bg-stone-50'
            }`}
            data-testid={`buch-student-mode-toggle-${materialId}`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${studentMode ? 'bg-white' : 'bg-emerald-500'}`} />
            {studentMode
              ? (isDe ? 'Schüleransicht aktiv' : 'Vista de alumno activa')
              : (isDe ? 'Als Schüler ansehen' : 'Ver como alumno')}
          </button>
        </div>
        {studentMode ? (
          <StudentComposer materialId={materialId} existing={null} onSaved={() => { /* preview-only */ }} isDe={isDe} previewOnly />
        ) : submissions.length === 0 ? (
          <p className="text-xs text-[#A8A29E] italic">{isDe ? 'Noch keine Abgaben.' : 'Aún no hay entregas.'}</p>
        ) : (
          submissions.map(s => (
            <SubmissionCard key={s.submission_id} submission={s} isTeacher={true} materialId={materialId} onChanged={load} isDe={isDe} />
          ))
        )}
      </div>
    );
  }

  // Student: at most one submission (their own).
  const own = submissions[0];
  return (
    <div className="mt-3 space-y-2" data-testid={`buch-uebungen-panel-${materialId}`}>
      {own && <SubmissionCard submission={own} isTeacher={false} materialId={materialId} onChanged={load} isDe={isDe} />}
      <StudentComposer materialId={materialId} existing={own} onSaved={() => load()} isDe={isDe} />
    </div>
  );
}
